/**
 * WebSocket-First Bill Processing Service
 *
 * Flow:
 * 1. prepare(file) - Upload file, get run_id
 * 2. connect(runId) - Connect WebSocket
 * 3. start(runId) - Start processing (all messages via WS)
 */

import Cookies from 'js-cookie';

const API_BASE = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';

class WSBillService {
  constructor() {
    this.ws = null;
    this.runId = null;
    this.token = null;
    this.listeners = {
      message: [],
      connected: [],
      disconnected: [],
      error: [],
      workflow_started: [],
      step_started: [],
      step_completed: [],
      chat_message: [],
      workflow_completed: []
    };
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.pingInterval = null;
  }

  /**
   * Step 1: Prepare workflow - upload file, get run_id
   */
  async prepare(file) {
    this.token = Cookies.get('sankalvax_token');
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    const formData = new FormData();
    formData.append('file', file);

    console.log('📄 WS-FIRST: Preparing workflow...');

    const response = await fetch(`${API_BASE}/api/ws-bill/prepare`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    const result = await response.json();

    if (result.success) {
      this.runId = result.run_id;
      console.log('📄 WS-FIRST: Prepared - run_id:', this.runId);
    }

    return result;
  }

  /**
   * Step 2: Connect WebSocket
   */
  connect(runId = null) {
    if (runId) this.runId = runId;
    if (!this.runId) {
      console.error('❌ WS-FIRST: No run_id for WebSocket connection');
      return false;
    }

    this.token = this.token || Cookies.get('sankalvax_token');
    if (!this.token) {
      console.error('❌ WS-FIRST: No token for WebSocket connection');
      return false;
    }

    // Close existing connection
    if (this.ws) {
      this.ws.close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/bill-processing-ws/ws/${this.runId}?token=${this.token}`;

    console.log('🔌 WS-FIRST: Connecting WebSocket...', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('✅ WS-FIRST: WebSocket connected');
      this.reconnectAttempts = 0;
      this._emit('connected', { runId: this.runId });

      // Start ping interval
      this.pingInterval = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 WS-FIRST: Message received:', data.type, data);

        this._emit('message', data);

        // Emit specific event types
        if (this.listeners[data.type]) {
          this._emit(data.type, data);
        }
      } catch (e) {
        console.error('❌ WS-FIRST: Parse error:', e);
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WS-FIRST: WebSocket error:', error);
      this._emit('error', { error });
    };

    this.ws.onclose = (event) => {
      console.log('🔌 WS-FIRST: WebSocket closed:', event.code, event.reason);

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }

      this._emit('disconnected', { code: event.code, reason: event.reason });

      // Auto-reconnect if not intentional close
      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`🔄 WS-FIRST: Reconnecting (attempt ${this.reconnectAttempts})...`);
        setTimeout(() => this.connect(), 2000 * this.reconnectAttempts);
      }
    };

    return true;
  }

  /**
   * Step 3: Start workflow processing
   */
  async start(runId = null) {
    if (runId) this.runId = runId;
    if (!this.runId) {
      throw new Error('No run_id - call prepare() first');
    }

    this.token = this.token || Cookies.get('sankalvax_token');

    console.log('🚀 WS-FIRST: Starting workflow...');

    const response = await fetch(`${API_BASE}/api/ws-bill/start/${this.runId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json();
    console.log('🚀 WS-FIRST: Start result:', result);

    return result;
  }

  /**
   * Get workflow status
   */
  async getStatus(runId = null) {
    const id = runId || this.runId;
    if (!id) return null;

    this.token = this.token || Cookies.get('sankalvax_token');

    const response = await fetch(`${API_BASE}/api/ws-bill/status/${id}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    return await response.json();
  }

  /**
   * Get all messages (for reconnection)
   */
  async getMessages(runId = null) {
    const id = runId || this.runId;
    if (!id) return null;

    this.token = this.token || Cookies.get('sankalvax_token');

    const response = await fetch(`${API_BASE}/api/ws-bill/messages/${id}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    return await response.json();
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }

    this.runId = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Subscribe to events
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from events
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event to listeners
   */
  _emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(cb => cb(data));
    }
  }
}

export default WSBillService;
